import React, { useState } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonIcon,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonButtons,
  IonButton,
  IonBackButton,
} from "@ionic/react";
import {
  trophyOutline,
  peopleOutline,
  starOutline,
  medalOutline,
  chevronDownOutline,
  chevronUpOutline,
  expandOutline,
  contractOutline,
} from "ionicons/icons";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import api from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { useI18n } from "@/i18n";

interface LeagueStanding {
  playerId: number;
  username: string;
  displayName: string;
  totalPoints: number;
  matchesPredicted: number;
  exactScores: number;
  goalDifferences: number;
  tendencies: number;
  position: number;
}

interface League {
  id: number;
  name: string;
  tier: number;
  division: string;
  max_members: number;
  season_name: string;
  current_members: number;
  standings: LeagueStanding[];
  total_standings: number;
}

interface Season {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface FinalRound {
  id: number;
  round_number: number;
  start_matchday: number;
  end_matchday: number;
  status: string;
}

interface FinalStandingsResponse {
  season: Season;
  final_round: FinalRound;
  leagues: League[];
  total_leagues: number;
}

const rankClass = (pos: number) => {
  if (pos === 1) return "rank-badge rank-1";
  if (pos === 2) return "rank-badge rank-2";
  if (pos === 3) return "rank-badge rank-3";
  return "rank-badge rank-default";
};

const getTierIcon = (tier: number) => {
  switch (tier) {
    case 1:
      return trophyOutline;
    case 2:
      return medalOutline;
    case 3:
      return starOutline;
    default:
      return peopleOutline;
  }
};

const SeasonStandings: React.FC = () => {
  const { seasonId } = useParams<{ seasonId: string }>();
  const [expandedLeagues, setExpandedLeagues] = useState<Set<number>>(
    new Set()
  );
  const [allExpanded, setAllExpanded] = useState(false);
  const { player } = useAuthStore();
  const { t } = useI18n();

  const { data, isLoading, error, refetch } =
    useQuery<FinalStandingsResponse>(
      ["season-final-standings", seasonId],
      async () =>
        (await api.get(`/api/leagues/season/${seasonId}/final-standings`)).data,
      { enabled: !!seasonId }
    );

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const toggleLeague = (id: number) => {
    const next = new Set(expandedLeagues);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedLeagues(next);
  };

  const leagues = data?.leagues || [];

  const expandAll = () => {
    setExpandedLeagues(new Set(leagues.map((l) => l.id)));
    setAllExpanded(true);
  };
  const collapseAll = () => {
    setExpandedLeagues(new Set());
    setAllExpanded(false);
  };

  const pageTitle = data
    ? `${data.season.name} — ${t("finalStandings")}`
    : t("finalStandings");

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/history" />
            </IonButtons>
            <IonTitle>{t("finalStandings")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonLoading isOpen message={t("loadingLeagues")} />
        </IonContent>
      </IonPage>
    );
  }

  if (error || !data) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/history" />
            </IonButtons>
            <IonTitle>{t("finalStandings")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText color="danger">
            <p>{t("errorLoadingLeagues")}</p>
          </IonText>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/history" />
          </IonButtons>
          <IonTitle>{pageTitle}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              fill="clear"
              onClick={allExpanded ? collapseAll : expandAll}
            >
              <IonIcon
                icon={allExpanded ? contractOutline : expandOutline}
                slot="icon-only"
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div style={{ padding: "12px 16px" }}>
          {/* Season summary banner */}
          <div
            style={{
              padding: "14px 16px",
              marginBottom: "12px",
              borderRadius: "var(--app-radius)",
              background:
                "linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 100%)",
              color: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <IonIcon icon={trophyOutline} style={{ fontSize: "18px" }} />
              <strong style={{ fontSize: "16px" }}>{data.season.name}</strong>
            </div>
            <p style={{ margin: 0, opacity: 0.85, fontSize: "13px" }}>
              {t("finalRound")}: {t("roundLabel")} {data.final_round.round_number}{" "}
              ({t("matchdaysLabel")} {data.final_round.start_matchday}-
              {data.final_round.end_matchday})
            </p>
          </div>

          <p
            style={{
              margin: "0 0 8px",
              color: "var(--ion-color-medium)",
              fontSize: "13px",
            }}
          >
            {leagues.length} {t("leaguesInSystem")}
          </p>

          {leagues.map((league) => {
            const isExpanded = expandedLeagues.has(league.id);
            const tierColor =
              league.tier === 1
                ? "var(--app-gold)"
                : league.tier === 2
                ? "var(--app-silver)"
                : "var(--app-bronze)";
            return (
              <IonCard key={league.id} style={{ margin: "0 0 10px 0" }}>
                <div
                  onClick={() => toggleLeague(league.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 14px",
                    cursor: "pointer",
                    background: `linear-gradient(90deg, ${tierColor}22 0%, transparent 100%)`,
                    borderBottom: isExpanded
                      ? "1px solid var(--ion-border-color)"
                      : "none",
                  }}
                >
                  <IonIcon
                    icon={getTierIcon(league.tier)}
                    style={{ fontSize: "22px", color: tierColor }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: "14px" }}>{league.name}</strong>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--ion-color-medium)",
                      }}
                    >
                      {league.total_standings} {t("playersLabel")}
                    </div>
                  </div>
                  <IonBadge
                    style={{
                      "--background": tierColor,
                      fontSize: "10px",
                      color: league.tier === 1 ? "#000" : "#fff",
                    }}
                  >
                    {t("tierLabel")} {league.tier}
                  </IonBadge>
                  <IonIcon
                    icon={isExpanded ? chevronUpOutline : chevronDownOutline}
                    style={{
                      fontSize: "18px",
                      color: "var(--ion-color-medium)",
                    }}
                  />
                </div>

                {isExpanded && (
                  <IonCardContent style={{ padding: 0 }}>
                    {league.standings.length > 0 ? (
                      <IonList lines="full" style={{ padding: 0 }}>
                        {league.standings.map((s) => {
                          const isCurrent =
                            player && s.playerId === player.id;
                          return (
                            <IonItem
                              key={s.playerId}
                              className={isCurrent ? "player-highlight" : ""}
                              style={{ "--min-height": "48px" }}
                            >
                              <div
                                slot="start"
                                className={rankClass(s.position)}
                              >
                                {s.position}
                              </div>
                              <IonLabel>
                                <h3
                                  style={{
                                    fontWeight: isCurrent ? 700 : 500,
                                    fontSize: "13px",
                                  }}
                                >
                                  {s.displayName || s.username}
                                  {isCurrent && (
                                    <span
                                      style={{
                                        color: "var(--app-gold)",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {t("youTag")}
                                    </span>
                                  )}
                                </h3>
                                <p
                                  className="egt-text"
                                  style={{
                                    color: "var(--ion-color-medium)",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "var(--app-score-exact)",
                                    }}
                                  >
                                    {s.exactScores}E
                                  </span>{" "}
                                  <span
                                    style={{
                                      color: "var(--app-score-diff)",
                                    }}
                                  >
                                    {s.goalDifferences}G
                                  </span>{" "}
                                  <span
                                    style={{
                                      color: "var(--app-score-tendency)",
                                    }}
                                  >
                                    {s.tendencies}T
                                  </span>
                                </p>
                              </IonLabel>
                              <div
                                slot="end"
                                className="tabular-nums"
                                style={{
                                  fontWeight: 700,
                                  fontSize: "14px",
                                }}
                              >
                                {s.totalPoints}
                              </div>
                            </IonItem>
                          );
                        })}
                      </IonList>
                    ) : (
                      <p
                        style={{
                          padding: "16px",
                          color: "var(--ion-color-medium)",
                          fontSize: "13px",
                        }}
                      >
                        {t("noPlayersInLeague")}
                      </p>
                    )}
                  </IonCardContent>
                )}
              </IonCard>
            );
          })}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SeasonStandings;
